import React, { useState, useEffect } from "react";
import SearchPort from "./SearchPort";
import ApiClient from "../services/fuelandmoreapi";
import type { TPort } from "../types";
import { config } from "../config";
import type { TPortNote } from "../types";
import BinIcon from "./BinIcon";

const Ports: React.FC = () => {
  const [selectedPort, setSelectedPort] = useState<TPort | null>(null);
  // Removed unused portLocked state
  const [note, setNote] = useState("");
  const [expirationDate, setExpirationDate] = useState(() => {
    const now = new Date();
    now.setHours(now.getHours() + 24);
    return now.toISOString().slice(0, 16);
  });
  const [notes, setNotes] = useState<TPortNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { apiBaseUrl } = config;
  const apiClient = new ApiClient(apiBaseUrl);

  // Load notes on startup
  useEffect(() => {
    setLoading(true);
    apiClient
      .getPortNotes()
      .then((data: TPortNote[]) => setNotes(data))
      .catch(() => setError("Failed to load notes."))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPort || !note) return;
    setLoading(true);
    try {
      await apiClient.savePortNote({
        unlocode: selectedPort.unlocode,
        note,
        expires: new Date(expirationDate),
      });
      setNote("");
      setExpirationDate(() => {
        const now = new Date();
        now.setHours(now.getHours() + 24);
        return now.toISOString().slice(0, 16);
      });
      setSelectedPort(null);
      // Refresh notes
      const updatedNotes = await apiClient.getPortNotes();
      setNotes(updatedNotes);
    } catch {
      setError("Failed to save note.");
    } finally {
      setLoading(false);
    }
  };

  function handleCancel() {
    setSelectedPort(null);
    setNote("");
    setExpirationDate(() => {
      const now = new Date();
      now.setHours(now.getHours() + 24);
      return now.toISOString().slice(0, 16);
    });
  }

  const handleDelete = async (guid: string) => {
    setLoading(true);
    try {
      await apiClient.deletePortNote(guid);
      const updatedNotes = await apiClient.getPortNotes();
      setNotes(updatedNotes);
    } catch {
      setError("Failed to delete note.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ports-container">
      <div className="add-note-section">
        <h3>Add Port Note</h3>
        <form onSubmit={handleSubmit} className="bunker-price-form">
          <div className="form-group">
            <label htmlFor="port-search">Port</label>
            {!selectedPort ? (
              <SearchPort onSelect={setSelectedPort} />
            ) : (
              <div
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <span className="selected-port-label">
                  {selectedPort.name} ({selectedPort.unlocode})
                </span>
              </div>
            )}
          </div>
          {selectedPort && (
            <>
              <div className="form-group">
                <label htmlFor="note-input">Note</label>
                <input
                  id="note-input"
                  type="text"
                  placeholder="Note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="expiration-input">Expiration Date</label>
                <input
                  id="expiration-input"
                  type="datetime-local"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  required
                />
              </div>
              <div
                className="form-actions"
                style={{ display: "flex", gap: "1rem" }}
              >
                <button
                  type="button"
                  className="secondary-button"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={loading}
                >
                  Submit
                </button>
              </div>
            </>
          )}
        </form>
      </div>
      <br />
      <div className="notes-table-section">
        <h3>Existing Notes</h3>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <table className="bunker-prices-table">
            <thead>
              <tr>
                <th>Port</th>
                <th>Note</th>
                <th>Created</th>
                <th>Expiration</th>
                <th>User Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {notes.map((n) => (
                <tr key={n.id}>
                  <td>{n.unlocode}</td>
                  <td>{n.note}</td>
                  <td>{formatDate(n.created ?? "")}</td>
                  <td>{formatDate(n.expires ?? "")}</td>
                  <td>{n.createdByEmail}</td>
                  <td>
                    <button
                      title="Delete"
                      aria-label={`Delete note for ${n.unlocode}`}
                      className="icon-button"
                      onClick={() => handleDelete(n.id ?? "")}
                      type="button"
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <BinIcon color="black" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
  function formatDate(date: Date | string) {
    const d = typeof date === "string" ? new Date(date) : date;
    const pad = (n: number) => n.toString().padStart(2, "0");
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${pad(d.getDate())}-${months[d.getMonth()]}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
};

export default Ports;
